import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { WorkerPanel } from "@/components/WorkerPanel";
import { OcrQueue } from "@/components/OcrQueue";
import { DEST_CODES, EMPTY_DEST, destLabel } from "@/lib/destinations";
import { cargoLabel, useT, trackLabel, stationName } from "@/i18n";
import { LangSwitcher } from "./LangSwitcher";
import { siteUrl, bookingUrl } from "@/lib/siteUrl";
import { fmtDwell } from "@/lib/wagonColors";
import { toast } from "sonner";
import {
  ShieldCheck,
  HardHat,
  Route,
  ClipboardList,
  ScanLine,
  ChevronRight,
  ArrowLeft,
  Save,
  Radar,
} from "lucide-react";

const TABS = [
  { id: "paths", icon: Route, labelKey: "tabPaths" },
  { id: "intake", icon: ClipboardList, labelKey: "intake" },
  { id: "ocr", icon: ScanLine, labelKey: "tabOcr" },
];

export const WorkerApp = ({
  yard,
  ocr,
  lang,
  setLang,
  setRole,
  updateWagon,
  acceptTrain,
  resolveOcr,
}) => {
  const t = useT(lang);
  const [tab, setTab] = useState("paths");
  const [stCode, setStCode] = useState(yard.stations[0]?.code || "");
  const [view, setView] = useState("tracks");
  const [trackIdx, setTrackIdx] = useState(null);
  const [editWagonId, setEditWagonId] = useState(null);
  const [form, setForm] = useState(null);
  const [phoneFrameEl, setPhoneFrameEl] = useState(null);
  const [isDesktopFrame, setIsDesktopFrame] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches
  );

  const phoneFrameRef = useCallback((node) => {
    setPhoneFrameEl(node);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = () => setIsDesktopFrame(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const editWagon = useMemo(() => {
    if (!editWagonId) return null;
    for (const s of yard.stations)
      for (const tr of s.tracks) {
        const w = tr.wagons.find((x) => x.id === editWagonId);
        if (w) return w;
      }
    return null;
  }, [editWagonId, yard]);

  useEffect(() => {
    if (editWagon) setForm({ ...editWagon });
  }, [editWagon]);

  const station = useMemo(
    () => yard.stations.find((s) => s.code === stCode) || yard.stations[0],
    [yard, stCode]
  );

  const track = trackIdx !== null ? station?.tracks[trackIdx] : null;

  const openWagon = (w) => {
    setEditWagonId(w.id);
    setForm({ ...w });
  };

  const closeEditSheet = () => {
    setEditWagonId(null);
    setForm(null);
  };

  const saveWagon = () => {
    if (!editWagonId || !form) return;
    if (!editWagon) {
      toast.info(t("wagonDeparted"));
      closeEditSheet();
      return;
    }
    const brutto = parseFloat(form.weight_brutto);
    const netto = parseFloat(form.weight_netto);
    updateWagon(editWagon.id, {
      number: form.number,
      cargo: form.cargo,
      weight_brutto: Number.isNaN(brutto) ? editWagon.weight_brutto : brutto,
      weight_netto: Number.isNaN(netto) ? editWagon.weight_netto : netto,
      destination: form.destination,
    });
    toast.success(t("saved"));
    closeEditSheet();
  };

  const stationLabel = (s) => stationName(s.code, lang).replace(/[«»"]/g, "");

  return (
    <div className="min-h-screen w-full bg-[#050506] flex flex-col items-center md:justify-center md:py-8 md:px-4">
      <div className="w-full max-w-[390px] px-4 pt-4 pb-2 md:px-0 md:pt-0 md:pb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="grid place-items-center w-8 h-8 rounded-md bg-amber-500/15 text-amber-400 shrink-0">
            <Radar size={16} />
          </div>
          <div className="min-w-0">
            <h1 className="text-sm font-bold text-white truncate" style={{ fontFamily: "Chivo, sans-serif" }}>
              {t("appTitle")}
            </h1>
            <p className="text-[9px] text-zinc-500 truncate">{t("workerAppSub")}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            data-testid="role-dispatcher"
            onClick={() => setRole("dispatcher")}
            className="flex items-center gap-1 px-2 py-1.5 rounded-sm text-[10px] bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
          >
            <ShieldCheck size={12} /> {t("dispatcher")}
          </button>
          <LangSwitcher lang={lang} setLang={setLang} accent="amber" fullWidth={false} compact />
        </div>
      </div>

      <div
        ref={phoneFrameRef}
        className="worker-phone relative w-full flex-1 md:flex-none flex flex-col md:w-[390px] md:h-[844px] md:rounded-[44px] md:border-[10px] md:border-zinc-900 md:shadow-[0_24px_80px_rgba(0,0,0,0.65)] md:overflow-hidden bg-[#0a0a0c]"
        data-testid="worker-app"
      >
        <header className="flex items-center gap-2 px-4 py-3 border-b border-zinc-800 bg-[#0d0d10] shrink-0">
          <HardHat size={18} className="text-amber-400" />
          <span className="text-sm font-semibold text-white">{t("worker")}</span>
          <span className="ml-auto flex items-center gap-1 text-[9px] font-mono text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            {t("liveMode")}
          </span>
        </header>

        <main className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {tab === "paths" && (
            <PathsTab
              view={view}
              setView={setView}
              station={station}
              stations={yard.stations}
              stCode={stCode}
              setStCode={setStCode}
              track={track}
              trackIdx={trackIdx}
              setTrackIdx={setTrackIdx}
              stationLabel={stationLabel}
              t={t}
              lang={lang}
              onWagonOpen={openWagon}
            />
          )}
          {tab === "intake" && (
            <WorkerPanel yard={yard} t={t} lang={lang} onAcceptTrain={acceptTrain} compact />
          )}
          {tab === "ocr" && (
            <OcrQueue items={ocr} t={t} onResolve={resolveOcr} editable mobile />
          )}
        </main>

        <nav className="shrink-0 border-t border-zinc-800 bg-[#0d0d10] grid grid-cols-3 safe-bottom">
          {TABS.map(({ id, icon: Icon, labelKey }) => (
            <button
              key={id}
              type="button"
              data-testid={`worker-tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex flex-col items-center justify-center gap-1 py-3 min-h-[56px] transition-colors ${
                tab === id ? "text-amber-400 bg-amber-500/10" : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <span className="relative">
                <Icon size={20} />
                {id === "ocr" && ocr?.length > 0 && (
                  <span className="absolute -top-1.5 -right-2 min-w-4 h-4 px-0.5 grid place-items-center text-[8px] font-bold rounded-full bg-amber-500 text-black">
                    {ocr.length}
                  </span>
                )}
              </span>
              <span className="text-[10px] font-medium">{t(labelKey)}</span>
            </button>
          ))}
        </nav>

        <Sheet open={!!editWagonId} onOpenChange={(open) => !open && closeEditSheet()}>
          <SheetContent
            side="bottom"
            container={isDesktopFrame ? phoneFrameEl : undefined}
            className="bg-[#0d0d10] border-zinc-800 rounded-t-xl max-h-[85vh] md:max-h-[85%] overflow-y-auto"
          >
            <SheetHeader className="text-left mb-4">
              <SheetTitle className="text-white font-mono">
                {t("editWagon")} · {form?.number ?? editWagon?.number ?? ""}
              </SheetTitle>
              <SheetDescription className="sr-only">{t("editWagon")}</SheetDescription>
            </SheetHeader>
            {form && (
              <div className="space-y-4 pb-6">
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{t("cargo")}</Label>
                  <Select value={form.cargo} onValueChange={(v) => setForm((f) => ({ ...f, cargo: v }))}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-800 h-12 min-h-[44px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="coal">{t("coal")}</SelectItem>
                      <SelectItem value="empty">{t("empty")}</SelectItem>
                      <SelectItem value="foreign">{t("foreign")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{t("bruttoW")}</Label>
                    <Input
                      type="number"
                      value={form.weight_brutto}
                      onChange={(e) => setForm((f) => ({ ...f, weight_brutto: e.target.value }))}
                      className="bg-zinc-900 border-zinc-800 h-12 font-mono min-h-[44px]"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{t("nettoW")}</Label>
                    <Input
                      type="number"
                      value={form.weight_netto}
                      onChange={(e) => setForm((f) => ({ ...f, weight_netto: e.target.value }))}
                      className="bg-zinc-900 border-zinc-800 h-12 font-mono min-h-[44px]"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{t("destination")}</Label>
                  <Select value={form.destination} onValueChange={(v) => setForm((f) => ({ ...f, destination: v }))}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-800 h-12 min-h-[44px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DEST_CODES.concat(EMPTY_DEST).map((code) => (
                        <SelectItem key={code} value={code}>{destLabel(code, lang)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={saveWagon}
                  className="w-full h-12 gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold min-h-[44px]"
                >
                  <Save size={18} /> {t("save")}
                </Button>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>

      <div className="w-full max-w-[390px] px-4 py-3 md:px-0 flex flex-col items-center gap-1.5">
        <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          {t("demoBadge")}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px]">
          <a
            href={siteUrl(lang)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-site"
            className="text-amber-400 hover:text-amber-300"
          >
            {t("ctaSite")}
          </a>
          <a
            href={bookingUrl(lang)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-booking"
            className="text-zinc-500 hover:text-zinc-300"
          >
            {t("ctaBooking")}
          </a>
        </div>
      </div>

      <Toaster position="top-center" theme="dark" />
    </div>
  );
};

function PathsTab({
  view,
  setView,
  station,
  stations,
  stCode,
  setStCode,
  track,
  trackIdx,
  setTrackIdx,
  stationLabel,
  t,
  lang,
  onWagonOpen,
}) {
  if (view === "wagons" && track) {
    const wagons = track.wagons.filter((w) => w.phase !== "departing");
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-2 px-3 py-3 border-b border-zinc-800">
          <button type="button" onClick={() => setView("tracks")} className="min-w-[44px] min-h-[44px] grid place-items-center text-zinc-400">
            <ArrowLeft size={20} />
          </button>
          <div>
            <p className="text-sm font-semibold text-white">{trackLabel(track.idx, t)}</p>
            <p className="text-[10px] text-zinc-500 font-mono">{track.count}/{track.capacity}</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto no-scrollbar p-3 space-y-2">
          {wagons.length === 0 ? (
            <p className="text-center text-zinc-500 text-sm py-8">{t("noWagonsOnTrack")}</p>
          ) : (
            wagons.map((w) => {
              const dwell = w.dwell_seconds ?? 0;
              return (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => onWagonOpen(w)}
                  className="w-full flex items-center gap-3 p-3 min-h-[56px] rounded-lg bg-zinc-900/80 border border-zinc-800 hover:border-amber-500/40 text-left"
                  data-testid={`worker-wagon-${w.number}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-mono font-bold text-white text-sm">{w.number}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                      {fmtDwell(dwell, t)} · {w.weight_brutto} {t("tonneUnit")}
                    </p>
                  </div>
                  <Badge variant="outline" className="shrink-0 border-zinc-700 text-zinc-300 text-[10px]">
                    {cargoLabel(w.cargo, t)}
                  </Badge>
                  <ChevronRight size={16} className="text-zinc-600 shrink-0" />
                </button>
              );
            })
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-3 border-b border-zinc-800 space-y-2">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500">{t("selectStation")}</p>
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-1">
          {stations.map((s) => (
            <button
              key={s.code}
              type="button"
              data-testid={`worker-station-${s.code}`}
              onClick={() => {
                setStCode(s.code);
                setTrackIdx(null);
                setView("tracks");
              }}
              className={`shrink-0 px-3 py-2 min-h-[44px] rounded-full text-[11px] font-mono border transition-colors ${
                stCode === s.code
                  ? "bg-amber-500 text-black border-amber-500"
                  : "bg-zinc-900 text-zinc-400 border-zinc-800"
              }`}
            >
              {s.code}
            </button>
          ))}
        </div>
        <p className="text-xs text-zinc-400 truncate">{stationLabel(station)}</p>
      </div>
      <div className="flex-1 overflow-y-auto no-scrollbar p-3 space-y-2">
        {station?.tracks.map((tr, i) => (
          <button
            key={tr.id}
            type="button"
            data-testid={`worker-track-${tr.id}`}
            onClick={() => {
              setTrackIdx(i);
              setView("wagons");
            }}
            className="w-full flex items-center gap-3 p-4 min-h-[56px] rounded-lg bg-zinc-900/80 border border-zinc-800 hover:border-amber-500/40 text-left"
          >
            <div className="flex-1">
              <p className="text-sm font-semibold text-white">{trackLabel(tr.idx, t)}</p>
              <p className="text-[11px] text-zinc-500 font-mono mt-0.5">
                {tr.count}/{tr.capacity} {t("wagonsOnTrack")}
              </p>
            </div>
            <ChevronRight size={18} className="text-zinc-600" />
          </button>
        ))}
      </div>
    </div>
  );
}
